import React, { useEffect, useState } from 'react';

import { getCar } from '../../api/carsApi';
import { CHAVE_CADASTRO_VEICULO, descartarRascunho, listarRascunhos, type ResumoDoRascunho } from '../../api/rascunhosApi';
import { limparMidias } from '../../infrastructure/offline/rascunhoMidiasDb';
import { notify } from '../../shared/feedback/notify';
import { checklistTypeLabel } from '../../types/checklist';
import type { ChecklistType } from '../../types/checklist';
import ConfirmModal from '../modal/confirm/ConfirmModal';
import '../../shared/feedback/aviso-de-tela.css';

export type RascunhoParaRetomar =
  | { tipo: 'CADASTRO' }
  | { tipo: 'CHECKLIST'; carId: string; checklistType: ChecklistType };

interface Props {
  /** Mude para reler a lista (ex.: ao fechar um modal que pode ter criado ou apagado rascunho). */
  versao: number;
  onRetomar: (r: RascunhoParaRetomar) => void;
}

interface Item {
  chave: string;
  alvo: RascunhoParaRetomar;
  descricao: string;
  atualizadoEm: string;
}

const interpretar = (r: ResumoDoRascunho): Omit<Item, 'descricao'> | null => {
  if (r.chave === CHAVE_CADASTRO_VEICULO) return { chave: r.chave, atualizadoEm: r.atualizadoEm, alvo: { tipo: 'CADASTRO' } };
  const m = /^checklist:(.+):([A-Z_]+)$/.exec(r.chave);
  if (!m) return null;
  return { chave: r.chave, atualizadoEm: r.atualizadoEm, alvo: { tipo: 'CHECKLIST', carId: m[1], checklistType: m[2] as ChecklistType } };
};

/**
 * Formulários em preenchimento guardados como rascunho (oficina/at-136). Depois que a versão nova
 * do sistema recarrega a página, é por aqui que a recepção retoma o cadastro ou o checklist que
 * estava fazendo.
 */
const AvisoRascunhoPendente: React.FC<Props> = ({ versao, onRetomar }) => {
  const [itens, setItens] = useState<Item[]>([]);
  const [paraDescartar, setParaDescartar] = useState<Item | null>(null);

  useEffect(() => {
    let vivo = true;
    listarRascunhos()
      .then(async (lista) => {
        const base = lista.map(interpretar).filter((x): x is Omit<Item, 'descricao'> => x !== null);
        const comDescricao = await Promise.all(
          base.map(async (b): Promise<Item> => {
            if (b.alvo.tipo === 'CADASTRO') return { ...b, descricao: 'Cadastro de veículo' };
            const rotulo = `Checklist de ${(checklistTypeLabel[b.alvo.checklistType] ?? b.alvo.checklistType).toLowerCase()}`;
            // A placa é só rótulo: sem ela o aviso continua útil.
            const placa = await getCar(b.alvo.carId).then((c: any) => c?.plate as string | undefined).catch(() => undefined);
            return { ...b, descricao: placa ? `${rotulo} da ${placa}` : rotulo };
          }),
        );
        if (vivo) setItens(comDescricao);
      })
      .catch(() => {
        // Não afirma "não há rascunho": avisa que não conseguiu verificar (at-60).
        if (vivo) {
          setItens([]);
          notify.warning('Não foi possível verificar se há cadastro ou checklist em andamento guardado. Recarregue a página para tentar de novo.', {
            toastId: 'rascunhos-falha-ao-listar',
          });
        }
      });
    return () => {
      vivo = false;
    };
  }, [versao]);

  if (!itens.length) return null;

  const descartar = async (item: Item) => {
    try {
      await descartarRascunho(item.chave);
      await limparMidias(item.chave).catch(() => undefined);
      setItens((atual) => atual.filter((i) => i.chave !== item.chave));
    } catch (error) {
      notify.actionError(error, { action: 'delete', subject: 'o rascunho' });
    }
  };

  return (
    <div className="aviso-de-tela" role="status">
      <span className="aviso-de-tela-titulo">Preenchimento em andamento</span>
      <ul>
        {itens.map((item) => (
          <li key={item.chave}>
            {item.descricao}, guardado em{' '}
            {new Date(item.atualizadoEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}.{' '}
            <button type="button" className="aviso-de-tela-link" onClick={() => onRetomar(item.alvo)}>
              Retomar
            </button>{' '}
            ou{' '}
            <button type="button" className="aviso-de-tela-link" onClick={() => setParaDescartar(item)}>
              descartar
            </button>
          </li>
        ))}
      </ul>
      <ConfirmModal
        isOpen={paraDescartar !== null}
        message={`Descartar o que foi guardado de "${paraDescartar?.descricao ?? ''}"? Não há como recuperar depois.`}
        confirmLabel="Descartar"
        onConfirm={() => {
          const alvo = paraDescartar;
          setParaDescartar(null);
          if (alvo) void descartar(alvo);
        }}
        onCancel={() => setParaDescartar(null)}
      />
    </div>
  );
};

export default AvisoRascunhoPendente;
